import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthModal from '../components/auth/AuthModal';
import Logo from '../components/ui/Logo';
import useDarkMode from '../hooks/useDarkMode';
import '../styles/landing.css';

const LAST_UPDATED = '19 April 2026';

const SECTIONS = [
  { id: 'who-we-are',      title: '1. Who we are' },
  { id: 'data-we-collect', title: '2. Data we collect' },
  { id: 'health-data',     title: '3. Health data (special category)' },
  { id: 'lawful-basis',    title: '4. Lawful basis for processing' },
  { id: 'how-we-use',      title: '5. How we use your data' },
  { id: 'sharing',         title: '6. Who we share data with' },
  { id: 'retention',       title: '7. How long we keep data' },
  { id: 'your-rights',     title: '8. Your rights' },
  { id: 'security',        title: '9. Security' },
  { id: 'storage',         title: '10. Local storage & cookies' },
  { id: 'children',        title: '11. Data about children' },
  { id: 'changes',         title: '12. Changes to this policy' },
  { id: 'contact',         title: '13. Contact & complaints' },
];

export default function PrivacyPolicyPage() {
  const navigate                = useNavigate();
  const { isDark, toggleDark }  = useDarkMode();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signup');

  const openLogin  = () => { setAuthMode('login');  setAuthOpen(true); };
  const openSignup = () => { setAuthMode('signup'); setAuthOpen(true); };

  return (
    <div className="landing-root">

      {/* Nav */}
      <nav className="landing-nav">
        <a href="/"><Logo variant="full" size={32} /></a>
        <div className="landing-nav-actions">
          <button className="btn btn-sm landing-nav-theme" onClick={toggleDark} aria-label="Toggle dark mode">
            {isDark ? '☀️' : '🌙'}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={openLogin}>Log in</button>
          <button className="btn btn-sm btn-primary"            onClick={openSignup}>Sign up free</button>
        </div>
      </nav>

      {/* Page header */}
      <div className="docs-page-header">
        <div className="landing-container">
          <h1 className="docs-page-title">Privacy Policy</h1>
          <p className="docs-page-sub">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      {/* Layout: TOC + content */}
      <div className="docs-layout">

        {/* Sidebar TOC */}
        <aside className="docs-sidebar">
          <div className="docs-sidebar-group">
            <p className="docs-sidebar-label">Contents</p>
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="docs-sidebar-link">
                {s.title}
              </a>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main className="docs-content">

          <div className="docs-section" style={{ marginBottom: '0.5rem' }}>
            <p className="docs-para">
              Baby Tracker ("we", "us", "our") is committed to protecting your privacy and the
              privacy of the children whose data is stored in the app. This policy explains what
              personal data we collect, why we collect it, how we use it, and what rights you have
              under the UK General Data Protection Regulation (UK GDPR) and the Data Protection
              Act 2018.
            </p>
            <p className="docs-para">
              Please read this policy carefully. By using Baby Tracker, you agree to the
              collection and use of data as described here.
            </p>
          </div>

          {/* 1. Who we are */}
          <section id="who-we-are" className="docs-section">
            <h2 className="docs-section-heading">1. Who we are</h2>
            <div className="docs-body">
              <p className="docs-para">
                Baby Tracker is operated by Kieron Harper, based in the United Kingdom.
                We are the <strong>data controller</strong> — the organisation responsible for
                deciding how and why personal data is processed.
              </p>
              <p className="docs-para">
                We are registered with the Information Commissioner's Office (ICO).
                Our ICO registration number is: <strong>[ICO REGISTRATION NUMBER — to be added once registered]</strong>.
              </p>
              <p className="docs-para">
                To contact us about data protection matters: <strong>[CONTACT EMAIL]</strong>
              </p>
            </div>
          </section>

          {/* 2. Data we collect */}
          <section id="data-we-collect" className="docs-section">
            <h2 className="docs-section-heading">2. Data we collect</h2>
            <div className="docs-body">
              <p className="docs-para">We collect the following categories of data:</p>

              <h3 className="docs-h3">Account data</h3>
              <ul className="docs-list">
                <li>Email address and password (stored securely via Firebase Authentication)</li>
                <li>Display name and caregiver role (e.g. Mum, Dad, Nan) — optional, set during onboarding</li>
                <li>Consent records and timestamps</li>
              </ul>

              <h3 className="docs-h3">Child profile data</h3>
              <ul className="docs-list">
                <li>Child's name, date of birth, and gender</li>
                <li>Profile photo (if uploaded)</li>
              </ul>

              <h3 className="docs-h3">Tracking data</h3>
              <ul className="docs-list">
                <li>Feeding logs (bottle feeds, breastfeeding, solid foods, water intake, food introductions)</li>
                <li>Sleep tracking logs (start time, end time, duration, type)</li>
                <li>Diaper/nappy change logs (type, notes)</li>
                <li>Growth measurements (weight, height, head circumference)</li>
                <li>Health records (temperature, medications, illnesses)</li>
                <li>Vaccinations and doctor visits</li>
                <li>Food allergies</li>
                <li>Developmental milestones</li>
                <li>Journal entries and photos</li>
              </ul>

              <h3 className="docs-h3">Usage data</h3>
              <ul className="docs-list">
                <li>Timestamps of when entries were logged and by which caregiver</li>
                <li>App settings and preferences (e.g. dashboard layout, dark mode)</li>
              </ul>

              <p className="docs-para">
                We do <strong>not</strong> collect analytics, advertising identifiers, device
                fingerprints, or any data beyond what is needed to operate the app.
              </p>
            </div>
          </section>

          {/* 3. Health data */}
          <section id="health-data" className="docs-section">
            <h2 className="docs-section-heading">3. Health data (special category)</h2>
            <div className="docs-body">
              <p className="docs-para">
                Some of the data Baby Tracker stores — including growth measurements, vaccinations,
                allergies, medications, doctor visit records, and health records — is classified as
                <strong> special category data</strong> under UK GDPR (Article 9). This is the
                highest-sensitivity tier of personal data, carrying additional legal protections.
              </p>
              <p className="docs-para">
                We process this data only with your <strong>explicit consent</strong>, given when
                you first use each health-related feature and at account creation. You can withdraw
                consent and delete this data at any time via the account settings.
              </p>
              <p className="docs-para">
                This data is used solely to provide the tracking and reporting features you
                request. It is never shared with third parties for any other purpose, never used
                for advertising, and never sold.
              </p>
            </div>
          </section>

          {/* 4. Lawful basis */}
          <section id="lawful-basis" className="docs-section">
            <h2 className="docs-section-heading">4. Lawful basis for processing</h2>
            <div className="docs-body">
              <p className="docs-para">We rely on the following lawful bases under UK GDPR:</p>
              <ul className="docs-list">
                <li>
                  <strong>Contract</strong> — processing your account and child profile data is
                  necessary to provide the service you have signed up for.
                </li>
                <li>
                  <strong>Explicit consent</strong> — for special category health data (Article 9),
                  we rely on your explicit consent given at signup and first use of health features.
                </li>
                <li>
                  <strong>Legitimate interests</strong> — for security logging and preventing abuse
                  of the service.
                </li>
              </ul>
            </div>
          </section>

          {/* 5. How we use data */}
          <section id="how-we-use" className="docs-section">
            <h2 className="docs-section-heading">5. How we use your data</h2>
            <div className="docs-body">
              <ul className="docs-list">
                <li>To operate the app and provide the tracking features you use</li>
                <li>To enable real-time data sharing between caregivers you have invited</li>
                <li>To generate summaries, insights, and PDF exports on your request</li>
                <li>To send reminders and notifications you have explicitly enabled</li>
                <li>To provide customer support if you contact us</li>
              </ul>
              <p className="docs-para">
                We do <strong>not</strong> use your data for advertising, profiling, automated
                decision-making, or any purpose beyond operating the service.
              </p>
            </div>
          </section>

          {/* 6. Sharing */}
          <section id="sharing" className="docs-section">
            <h2 className="docs-section-heading">6. Who we share data with</h2>
            <div className="docs-body">
              <p className="docs-para">
                We share data only with the following party, acting as our <strong>data processor</strong>:
              </p>
              <ul className="docs-list">
                <li>
                  <strong>Google Firebase (Google LLC)</strong> — provides our database
                  (Firestore), authentication, and hosting infrastructure. Data is stored in
                  EU/UK regions. Google processes data under a Data Processing Agreement in
                  accordance with UK GDPR.
                </li>
              </ul>
              <p className="docs-para">
                We do not sell, rent, or share your data with advertisers, data brokers, or any
                other third parties. We will only disclose data to law enforcement if required
                by law and will notify you where legally permitted to do so.
              </p>
            </div>
          </section>

          {/* 7. Retention */}
          <section id="retention" className="docs-section">
            <h2 className="docs-section-heading">7. How long we keep data</h2>
            <div className="docs-body">
              <ul className="docs-list">
                <li>
                  <strong>Active accounts</strong> — data is retained for as long as your account
                  is active.
                </li>
                <li>
                  <strong>Deleted accounts</strong> — when you delete your account, all associated
                  data (child profiles, tracking logs, journal entries, and account information) is
                  permanently deleted from our systems within 30 days.
                </li>
                <li>
                  <strong>Inactive accounts</strong> — accounts with no activity for 3 years may
                  be subject to deletion after prior notice by email.
                </li>
              </ul>
            </div>
          </section>

          {/* 8. Your rights */}
          <section id="your-rights" className="docs-section">
            <h2 className="docs-section-heading">8. Your rights</h2>
            <div className="docs-body">
              <p className="docs-para">
                Under UK GDPR you have the following rights. To exercise any of them, contact us
                at <strong>[CONTACT EMAIL]</strong>. We will respond within 30 days.
              </p>
              <ul className="docs-list">
                <li><strong>Right of access</strong> — request a copy of the personal data we hold about you.</li>
                <li><strong>Right to rectification</strong> — ask us to correct inaccurate data.</li>
                <li><strong>Right to erasure</strong> — ask us to delete your data. You can also do this directly via the account deletion option in app settings.</li>
                <li><strong>Right to data portability</strong> — receive your data in a structured, machine-readable format. Use the "Export all data" option in settings.</li>
                <li><strong>Right to restrict processing</strong> — ask us to pause processing your data in certain circumstances.</li>
                <li><strong>Right to object</strong> — object to processing based on legitimate interests.</li>
                <li><strong>Right to withdraw consent</strong> — withdraw consent for special category data at any time. This will not affect the lawfulness of processing before withdrawal.</li>
                <li><strong>Rights related to automated decision-making</strong> — we do not use automated decision-making or profiling.</li>
              </ul>
            </div>
          </section>

          {/* 9. Security */}
          <section id="security" className="docs-section">
            <h2 className="docs-section-heading">9. Security</h2>
            <div className="docs-body">
              <p className="docs-para">
                We take the security of your data seriously, particularly given that it includes
                health data about children. Our security measures include:
              </p>
              <ul className="docs-list">
                <li>All data in transit is encrypted via HTTPS/TLS</li>
                <li>Data at rest is encrypted by Firebase/Google infrastructure</li>
                <li>Access to data is enforced via Firestore security rules — caregivers can only access data for children they have been explicitly invited to</li>
                <li>Viewer-only access is enforced at the database level, not just the UI</li>
                <li>Passwords are never stored — authentication is handled by Firebase Auth</li>
              </ul>
              <p className="docs-para">
                In the event of a data breach that is likely to result in a risk to your rights
                and freedoms, we will notify the ICO within 72 hours and inform affected users
                without undue delay.
              </p>
            </div>
          </section>

          {/* 10. Local storage */}
          <section id="storage" className="docs-section">
            <h2 className="docs-section-heading">10. Local storage &amp; cookies</h2>
            <div className="docs-body">
              <p className="docs-para">
                Baby Tracker uses <strong>browser local storage</strong> (not cookies) to store
                functional preferences on your device, including:
              </p>
              <ul className="docs-list">
                <li>Dark mode preference</li>
                <li>Active sleep/breastfeeding timer state (so timers survive page refreshes)</li>
                <li>Onboarding tour completion status</li>
              </ul>
              <p className="docs-para">
                This storage is strictly functional — it is necessary for the app to work correctly
                and does not involve tracking or advertising. No third-party cookies or tracking
                scripts are used.
              </p>
              <p className="docs-para">
                Firebase may set session cookies for authentication purposes. These are essential
                to keeping you logged in and cannot be disabled without affecting your ability to
                use the service.
              </p>
            </div>
          </section>

          {/* 11. Children */}
          <section id="children" className="docs-section">
            <h2 className="docs-section-heading">11. Data about children</h2>
            <div className="docs-body">
              <p className="docs-para">
                Baby Tracker is designed for use by adults (parents, caregivers, and family members)
                to track data <em>about</em> children — children are the data subjects, not the users.
                The app is not intended to be used directly by children.
              </p>
              <p className="docs-para">
                As the parent or primary caregiver creating a child profile, you act as the
                responsible party for that child's data. You control who can access it via the
                caregiver invite system and can delete it at any time.
              </p>
              <p className="docs-para">
                We apply heightened care to data about children: it is never shared, never used
                for advertising, and is subject to the same deletion and export rights as adult
                account data.
              </p>
            </div>
          </section>

          {/* 12. Changes */}
          <section id="changes" className="docs-section">
            <h2 className="docs-section-heading">12. Changes to this policy</h2>
            <div className="docs-body">
              <p className="docs-para">
                We may update this policy from time to time. When we make material changes, we
                will notify you by email and update the "Last updated" date at the top of this
                page. Continued use of the app after changes take effect constitutes acceptance
                of the updated policy.
              </p>
            </div>
          </section>

          {/* 13. Contact */}
          <section id="contact" className="docs-section">
            <h2 className="docs-section-heading">13. Contact &amp; complaints</h2>
            <div className="docs-body">
              <p className="docs-para">
                For any questions about this policy, to exercise your rights, or to make a
                complaint, contact us at: <strong>[CONTACT EMAIL]</strong>
              </p>
              <p className="docs-para">
                If you are not satisfied with our response, you have the right to lodge a
                complaint with the Information Commissioner's Office (ICO):
              </p>
              <ul className="docs-list">
                <li>Website: <strong>ico.org.uk</strong></li>
                <li>Helpline: 0303 123 1113</li>
              </ul>
            </div>
          </section>

        </main>
      </div>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container landing-footer-inner">
          <Logo variant="full" size={28} />
          <nav className="landing-footer-links">
            <a href="/docs">Help</a>
            <a href="/roadmap">Roadmap</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <button className="landing-footer-btn-link" onClick={openLogin}>Log in</button>
          </nav>
        </div>
      </footer>

      {authOpen && (
        <AuthModal
          initialMode={authMode}
          onClose={() => setAuthOpen(false)}
          onSuccess={() => { setAuthOpen(false); navigate('/app'); }}
        />
      )}
    </div>
  );
}
